import React, { useEffect, useState, useCallback } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { API, useAuth } from '../App';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../contexts/ThemeContext';
import { 
  Menu, 
  X, 
  User,
  LogOut, 
  Settings, 
  MessageSquare,
  LayoutDashboard,
  Bell,
  ChevronDown,
  Bot,
  GitCompare,
  Code,
  Sun,
  Moon,
  Globe
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";

const Navbar = ({ disableSticky = false }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { t, i18n } = useTranslation();
  const { theme, toggleTheme, colors: C } = useTheme();
  const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [notifications, setNotifications] = useState([]);
  const [notifOpen, setNotifOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  
  useEffect(() => {
    let ticking = false;
    const handleScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        const currentY = window.scrollY;
        // Track if scrolled for background
        setScrolled(currentY > 50);
        ticking = false;
      });
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const changeLanguage = (lng) => {
    i18n.changeLanguage(lng);
    localStorage.setItem('pd-language', lng);
  };

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
  }, [theme]);

  const fetchUnread = useCallback(async () => {
    if (!user) return;
    try {
      const res = await axios.get(`${API}/notifications/unread-count`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      setUnreadCount(res.data?.count || 0);
    } catch {}
  }, [user]);

  const fetchNotifications = async () => {
    if (!user) return;
    try {
      const res = await axios.get(`${API}/notifications?limit=8`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      setNotifications(res.data || []);
    } catch {}
  };

  useEffect(() => {
    if (!user) return;
    fetchUnread();
    const interval = setInterval(fetchUnread, 30000);
    return () => clearInterval(interval);
  }, [fetchUnread, user]);

  const handleNotifOpen = (open) => {
    setNotifOpen(open);
    if (open) fetchNotifications();
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const getTierBadge = (tier) => {
    const badges = {
      free: null,
      licensed: <span className="px-2 py-0.5 text-xs rounded tier-licensed">Licensed</span>,
      featured: <span className="px-2 py-0.5 text-xs rounded tier-featured">Featured</span>,
      powerhouse: <span className="px-2 py-0.5 text-xs rounded tier-powerhouse">Powerhouse</span>
    };
    return badges[tier] || null;
  };

  const getTierHoverClass = (tier) => {
    switch (tier) {
      case 'licensed': return 'focus:bg-[#22D3EE]/10 focus:text-[#22D3EE] hover:bg-[#22D3EE]/10 hover:text-[#22D3EE]';
      case 'featured': return 'focus:bg-[#FBBF24]/10 focus:text-[#FBBF24] hover:bg-[#FBBF24]/10 hover:text-[#FBBF24]';
      case 'powerhouse': return 'focus:bg-[#8B5CF6]/10 focus:text-[#8B5CF6] hover:bg-[#8B5CF6]/10 hover:text-[#8B5CF6]';
      default: return 'focus:bg-white/10 focus:text-white hover:bg-white/10 hover:text-white';
    }
  };
  const tierHoverClass = user ? getTierHoverClass(user.subscription_tier) : 'focus:bg-white/10 focus:text-white hover:bg-white/10 hover:text-white';

  const isActive = (path) => {
    if (path === '/') return location.pathname === '/';
    if (path === '/directory') return location.pathname === '/directory';
    return location.pathname.startsWith(path);
  };

  const handleHashNav = (to) => {
    if (to.includes('#')) {
      const hash = to.split('#')[1];
      if (location.pathname === '/') {
        const el = document.getElementById(hash);
        if (el) { el.scrollIntoView({ behavior: 'smooth' }); return; }
      }
    }
  };

  const navLink = (to, label, testId) => (
    <Link
      to={to}
      className={`text-sm font-medium transition-colors relative py-1 ${
        isActive(to) ? '' : ''
      }`}
      style={{ color: isActive(to) ? 'var(--pd-purple)' : 'var(--pd-text2)' }}
      data-testid={testId}
      onClick={() => handleHashNav(to)}
    >
      {label}
      {isActive(to) && (
        <span className="absolute -bottom-1.5 left-1/2 -translate-x-1/2 w-5 h-[2px] rounded-full" style={{ background: 'var(--pd-purple)' }} />
      )}
    </Link>
  );

  const mobileLink = (to, label, onClick) => (
    <Link
      to={to}
      className={`block py-2.5 text-sm font-medium transition-colors`}
      style={{ color: isActive(to) ? 'var(--pd-purple)' : 'var(--pd-text2)' }}
      onClick={() => { handleHashNav(to); onClick?.(); }}
    >
      {label}
    </Link>
  );

  return (
    <nav
      className={`${disableSticky ? 'absolute' : 'fixed'} left-0 right-0 z-50`}
      style={{
        top: 0,
        background: scrolled ? 'var(--pd-nav-bg)' : 'transparent',
        backdropFilter: scrolled ? 'blur(16px)' : 'none',
        WebkitBackdropFilter: scrolled ? 'blur(16px)' : 'none',
        borderBottom: scrolled ? '1px solid var(--pd-border)' : 'none',
        transition: 'background 0.3s ease, backdrop-filter 0.3s ease, border-color 0.3s ease'
      }}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center logo-container" data-testid="navbar-logo">
            <img src="/logo-lockup.png" alt="Podcasters.Directory" className="h-10 sm:h-12 object-contain logo-dark" />
            <img src="/logo-light.png" alt="Podcasters.Directory" className="h-10 sm:h-12 object-contain logo-light" />
          </Link>

          {/* Desktop Navigation (Centered via absolute positioning) */}
          <div className="hidden md:flex items-center gap-6 absolute left-1/2 -translate-x-1/2">
            {navLink('/', t('nav.home'), 'nav-home')}
            {navLink('/about', 'About', 'nav-about')}
            {navLink('/directory', 'Podcasters', 'nav-podcasters')}
            {navLink('/features', 'Features', 'nav-features')}
            {navLink('/leaderboard', 'Leaderboard', 'nav-leaderboard')}
            {navLink('/blog', t('nav.blog', 'Blog'), 'nav-blog')}
          </div>

          {/* User Menu / Auth Buttons */}
          <div className="hidden md:flex items-center gap-2">


            {/* Language Selector */}
            <DropdownMenu modal={false}>
              <DropdownMenuTrigger asChild>
                <button className="p-2 rounded-lg transition-colors" style={{ color: 'var(--pd-muted)' }} data-testid="language-selector">
                  <Globe className="w-4 h-4" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="w-36 border" style={{ background: 'var(--pd-dropdown-bg)', borderColor: 'var(--pd-border)', color: 'var(--pd-text)' }} align="end">
                {[
                  { code: 'en', label: 'English', flag: 'EN' },
                  { code: 'es', label: 'Espanol', flag: 'ES' },
                  { code: 'fr', label: 'Francais', flag: 'FR' },
                  { code: 'pt', label: 'Portugues', flag: 'PT' },
                ].map(lang => (
                  <DropdownMenuItem key={lang.code} onSelect={() => changeLanguage(lang.code)}
                    className={`cursor-pointer focus:bg-white/10 focus:text-white hover:bg-white/10 gap-2 ${i18n.language === lang.code ? 'text-[#8B5CF6]' : ''}`}
                    data-testid={`lang-${lang.code}`}>
                    <span className="text-[10px] font-bold w-5">{lang.flag}</span> {lang.label}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>

            {/* Notification Bell - only on dashboard */}
            {user && location.pathname.startsWith('/dashboard') && (
              <DropdownMenu open={notifOpen} onOpenChange={handleNotifOpen} modal={false}>
                <DropdownMenuTrigger asChild>
                  <button className="relative p-2 rounded-lg hover:bg-white/10 transition-colors" data-testid="notif-bell">
                    <Bell className="w-5 h-5 text-white/60" />
                    {unreadCount > 0 && (
                      <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] flex items-center justify-center rounded text-[10px] font-bold text-white bg-red-500 px-1" data-testid="notif-count">
                        {unreadCount > 9 ? '9+' : unreadCount}
                      </span>
                    )}
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent className="w-80 border p-0" style={{ background: 'var(--pd-dropdown-bg)', borderColor: 'var(--pd-border)', color: 'var(--pd-text)' }} align="end">
                  <div className="px-4 py-3 border-b border-white/10 flex items-center justify-between">
                    <span className="text-sm font-semibold">Notifications</span>
                    {unreadCount > 0 && (
                      <span className="text-[10px] font-medium px-2 py-0.5 rounded bg-red-500/15 text-red-400">{unreadCount} new</span>
                    )}
                  </div>
                  <div className="max-h-[320px] overflow-y-auto">
                    {notifications.length === 0 ? (
                      <div className="px-4 py-8 text-center">
                        <Bell className="w-6 h-6 mx-auto mb-2 text-white/10" />
                        <p className="text-xs text-white/30">No notifications yet</p>
                      </div>
                    ) : notifications.map(n => (
                      <button key={n.id} onClick={() => { setNotifOpen(false); navigate(n.link || '/dashboard'); }}
                        className={`w-full text-left px-4 py-3 flex items-start gap-3 hover:bg-white/[0.04] transition-colors border-b border-white/[0.04] ${!n.is_read ? 'bg-white/[0.02]' : ''}`}
                        data-testid={`notif-item-${n.type}`}>
                        {n.actor_avatar ? (
                          <img src={n.actor_avatar} alt="" className="w-8 h-8 rounded-lg object-cover shrink-0 mt-0.5" />
                        ) : (
                          <div className="w-8 h-8 rounded-lg shrink-0 mt-0.5 flex items-center justify-center"
                            style={{ background: n.type === 'match_request' ? 'rgba(139,92,246,0.12)' : n.type === 'match_accepted' ? 'rgba(16,185,129,0.12)' : 'rgba(34,211,238,0.12)' }}>
                            {n.type === 'match_request' ? <User className="w-3.5 h-3.5 text-purple-400" /> : 
                             n.type === 'match_accepted' ? <User className="w-3.5 h-3.5 text-green-400" /> : 
                             <MessageSquare className="w-3.5 h-3.5 text-cyan-400" />}
                          </div>
                        )}
                        <div className="flex-1 min-w-0">
                          <p className="text-xs font-medium text-white truncate">{n.title}</p>
                          <p className="text-[11px] text-white/40 truncate">{n.message}</p>
                          {n.created_at && (
                            <p className="text-[10px] text-white/20 mt-0.5">
                              {new Date(n.created_at).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
                            </p>
                          )}
                        </div>
                        {!n.is_read && <div className="w-2 h-2 rounded-full bg-purple-500 shrink-0 mt-2" />}
                      </button>
                    ))}
                  </div>
                  <div className="px-4 py-2.5 border-t border-white/10">
                    <button onClick={() => { setNotifOpen(false); navigate('/dashboard?tab=matches'); }}
                      className="text-[11px] font-medium text-purple-400 hover:text-purple-300 transition-colors w-full text-center">
                      View all activity
                    </button>
                  </div>
                </DropdownMenuContent>
              </DropdownMenu>
            )}

            {user ? (
              <DropdownMenu modal={false}>
                <DropdownMenuTrigger asChild>
                  <Button 
                    variant="ghost" 
                    className="flex items-center gap-2"
                    style={{ color: 'var(--pd-text)' }}
                    data-testid="user-menu-trigger"
                  >
                    {user.avatar_url ? (
                      <img src={user.avatar_url} alt={user.name} className="w-8 h-8 rounded-full object-cover ring-1 ring-white/20" />
                    ) : (
                      <div className="w-8 h-8 rounded-full flex items-center justify-center" style={{ background: 'var(--pd-purple)' }}>
                        <User className="w-4 h-4 text-white" />
                      </div>
                    )}
                    <span>{user.name}</span>
                    {getTierBadge(user.subscription_tier)}
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent className="w-56 border" style={{ background: 'var(--pd-dropdown-bg)', borderColor: 'var(--pd-border)', color: 'var(--pd-text)' }} align="end" sideOffset={8}>
                  <DropdownMenuItem asChild className={`cursor-pointer ${tierHoverClass}`}>
                    <Link to={user?.is_admin ? '/admin' : '/dashboard'} className="flex w-full items-center">
                      <LayoutDashboard className="mr-2 h-4 w-4" />
                      {user?.is_admin ? 'Admin Panel' : 'Dashboard'}
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuSeparator className="bg-white/10" />
                  <DropdownMenuItem onSelect={handleLogout} className="cursor-pointer focus:bg-red-500/10 focus:text-red-400 hover:bg-red-500/10 hover:text-red-400 text-red-400">
                    <LogOut className="mr-2 h-4 w-4" />
                    {t('nav.logout')}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <div className="flex items-center gap-3">
                <Link to="/auth">
                  <Button variant="ghost" className="text-sm font-medium" style={{ color: 'var(--pd-text2)' }} data-testid="login-btn">
                    {t('nav.login', 'Log in')}
                  </Button>
                </Link>
                <Link to="/auth?mode=register" className="btn-pill" data-testid="signup-btn">
                  {t('nav.signup', 'Get Started')}
                </Link>
              </div>
            )}
          </div>

          {/* Mobile menu button */}
          <div className="md:hidden flex items-center gap-1">
            {user && location.pathname.startsWith('/dashboard') && (
              <button onClick={() => { navigate('/dashboard?tab=matches'); setMobileMenuOpen(false); }}
                className="relative p-2 rounded-lg hover:bg-white/10 transition-colors" data-testid="notif-bell-mobile">
                <Bell className="w-5 h-5 text-white/60" />
                {unreadCount > 0 && (
                  <span className="absolute -top-0.5 -right-0.5 min-w-[16px] h-[16px] flex items-center justify-center rounded text-[9px] font-bold text-white bg-red-500 px-0.5">
                    {unreadCount > 9 ? '9+' : unreadCount}
                  </span>
                )}
              </button>
            )}
            <button
              className="p-2 rounded-lg hover:bg-white/10 transition-colors"
              style={{ color: 'var(--pd-text)' }}
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="mobile-menu-btn"
              aria-label="Toggle menu"
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="md:hidden backdrop-blur-2xl backdrop-saturate-150 border-t" style={{ background: 'var(--pd-nav-bg)', borderColor: 'var(--pd-border)', WebkitBackdropFilter: 'blur(24px) saturate(150%)' }}>
          <div className="px-4 py-4 space-y-1">
            {mobileLink('/', t('nav.home'), () => setMobileMenuOpen(false))}
            {mobileLink('/about', 'About', () => setMobileMenuOpen(false))}
            {mobileLink('/directory', 'Podcasters', () => setMobileMenuOpen(false))}
            {mobileLink('/features', 'Features', () => setMobileMenuOpen(false))}
            {mobileLink('/leaderboard', 'Leaderboard', () => setMobileMenuOpen(false))}
            {mobileLink('/blog', t('nav.blog', 'Blog'), () => setMobileMenuOpen(false))}

            {/* Language */}
            <div className="pt-4 mt-2 border-t flex items-center gap-2" style={{ borderColor: 'var(--pd-border)' }}>
              <DropdownMenu modal={false}>
                <DropdownMenuTrigger asChild>
                  <button
                    className="flex items-center justify-center gap-2 py-2.5 px-4 rounded-xl border text-sm font-medium transition-colors hover:bg-white/5"
                    style={{ color: 'var(--pd-text)', borderColor: 'var(--pd-border)' }}
                    data-testid="mobile-language-selector"
                  >
                    <Globe className="w-4 h-4" />
                    <span className="text-[11px] font-bold uppercase">{i18n.language || 'EN'}</span>
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent className="w-36 border" style={{ background: 'var(--pd-dropdown-bg)', borderColor: 'var(--pd-border)', color: 'var(--pd-text)' }} align="end">
                  {[
                    { code: 'en', label: 'English', flag: 'EN' },
                    { code: 'es', label: 'Espanol', flag: 'ES' },
                    { code: 'fr', label: 'Francais', flag: 'FR' },
                    { code: 'pt', label: 'Portugues', flag: 'PT' },
                  ].map(lang => (
                    <DropdownMenuItem key={lang.code} onSelect={() => changeLanguage(lang.code)}
                      className={`cursor-pointer focus:bg-white/10 focus:text-white hover:bg-white/10 gap-2 ${i18n.language === lang.code ? 'text-[#8B5CF6]' : ''}`}
                      data-testid={`mobile-lang-${lang.code}`}>
                      <span className="text-[10px] font-bold w-5">{lang.flag}</span> {lang.label}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>

            <div className="pt-4 mt-2 border-t" style={{ borderColor: 'var(--pd-border)' }}>
              {user ? (
                <div className="space-y-1">
                  {mobileLink(user?.is_admin ? '/admin' : '/dashboard', user?.is_admin ? 'Admin Panel' : 'Dashboard', () => setMobileMenuOpen(false))}
                  <div className="pt-3 mt-2 border-t" style={{ borderColor: 'var(--pd-border)' }}>
                    <button
                      onClick={() => { handleLogout(); setMobileMenuOpen(false); }}
                      className="block py-2.5 text-sm font-medium text-red-400"
                      data-testid="mobile-logout-btn"
                    >
                      {t('nav.logout')}
                    </button>
                  </div>
                </div>
              ) : (
                <div className="space-y-3">
                  <Link to="/auth" onClick={() => setMobileMenuOpen(false)}>
                    <Button variant="ghost" className="w-full" style={{ color: 'var(--pd-text)' }}>{t('nav.login', 'Log in')}</Button>
                  </Link>
                  <Link to="/auth?mode=register" onClick={() => setMobileMenuOpen(false)}>
                    <Button className="w-full btn-primary">{t('nav.signup', 'Get Started')}</Button>
                  </Link>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;
